import gql from 'graphql-tag';
// @ts-ignore
import { dataIdFromObject } from 'lib/apollo/cache/memory';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import RemindAPI from 'lib/RemindAPI';
import ADD_STUDENT_MUTATION from 'coaching/mutations/AddStudentToCoachingByDevice.gql';
import CREATE_COACH_MUTATION from 'coaching/mutations/CreateCoach.gql';
import FIND_OR_CREATE_COACHING_STREAM_MUTATION from 'coaching/mutations/FindOrCreateCoachingStream.gql';
import GET_COACH_LIST_QUERY from 'coaching/queries/GetCoachList.gql';
import GET_NAME_FOR_USER_UUID_QUERY from 'coaching/queries/GetNameForUserUuid.gql';
import GET_COACH_AVAILABILITY_FOR_EVENT_QUERY from 'coaching/queries/GetCoachAvailabilityForEvent.gql';
import DELETE_COACHING_SESSION_MUTATION from 'coaching/mutations/DeleteCoachingSession.gql';
import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';
import PATCH_PARTICIPANT_MUTATION from 'coaching/mutations/PatchParticipant.gql';
import PATCH_COACH_MUTATION from 'coaching/mutations/PatchCoach.gql';
import PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithConnectedStudent.gql';
import PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithNoConnectedStudent.gql';
import {
  CoachingUserType,
  CoachingEventDefinitionInput,
  CoachingParticipantInput,
  CoachingCoachInput,
} from 'globalTypes';
import {
  AddStudentToCoachingByDevice,
  AddStudentToCoachingByDeviceVariables,
} from 'coaching/mutations/__generated__/AddStudentToCoachingByDevice';
import { CreateCoach, CreateCoachVariables } from 'coaching/mutations/__generated__/CreateCoach';
import {
  DeleteCoachingSession,
  DeleteCoachingSessionVariables,
} from 'coaching/mutations/__generated__/DeleteCoachingSession';
import {
  FindOrCreateCoachingStream,
  FindOrCreateCoachingStreamVariables,
} from 'coaching/mutations/__generated__/FindOrCreateCoachingStream';
import {
  GetCoachAvailabilityForEvent,
  GetCoachAvailabilityForEventVariables,
} from 'coaching/queries/__generated__/GetCoachAvailabilityForEvent';
import {
  GetNameForUserUuid,
  GetNameForUserUuidVariables,
} from 'coaching/queries/__generated__/GetNameForUserUuid';
import {
  PatchParticipant,
  PatchParticipantVariables,
} from 'coaching/mutations/__generated__/PatchParticipant';
import { PatchCoach, PatchCoachVariables } from 'coaching/mutations/__generated__/PatchCoach';
import {
  PurchaseCoachingProductWithConnectedStudent,
  PurchaseCoachingProductWithConnectedStudentVariables,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithConnectedStudent';
import {
  PurchaseCoachingProductWithNoConnectedStudent,
  PurchaseCoachingProductWithNoConnectedStudentVariables,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithNoConnectedStudent';
import { errorFragment } from 'queries/__generated__/errorFragment';
import ToastActions from 'actions/ToastActions';
import { Student } from 'coaching/containers/GetProspectiveStudentOptionsContainer';

export function handleGQLError(error: errorFragment, consoleErrorText: string) {
  console.warn(error);
  console.error(`Coaching - ${consoleErrorText}`);
  ToastActions.showGQLError(error);
}

export function getCoachAvailabilityForEvent(
  coachUuid: string,
  eventDefinition: CoachingEventDefinitionInput,
  participantUuid: string
) {
  return RemindAPI.execute<GetCoachAvailabilityForEvent, GetCoachAvailabilityForEventVariables>({
    query: GET_COACH_AVAILABILITY_FOR_EVENT_QUERY,
    variables: {
      coachUuid,
      eventDefinition,
      participantUuid,
    },
    forceFetch: true,
  }).catch(err => {
    console.warn(err);
    console.error(
      `CoachingActions - could not get coach availability for event coach_uuid=${coachUuid} start_time=${eventDefinition.startDateTime} end_time=${eventDefinition.endDateTime} recurrence=${eventDefinition.recurrence}`
    );
  });
}

export function deleteCoachingSession(
  userUuid: string,
  userType: CoachingUserType,
  coachingSessionUuid: string,
  cancellationReason?: string,
  instanceEventId?: string,
  instanceStartDateTime?: string
) {
  const errorText = `CoachingActions - could not delete session_uuid=${coachingSessionUuid} instance_id=${instanceEventId}`;

  return RemindAPI.execute<DeleteCoachingSession, DeleteCoachingSessionVariables>({
    mutation: DELETE_COACHING_SESSION_MUTATION,
    variables: {
      coachingSessionUuid,
      instanceEventId,
      instanceStartDateTime,
      cancellationReason,
    },
    refetchQueries: [
      {
        query: GET_COACHING_SESSIONS_QUERY,
        variables: { userUuid, userType },
      },
    ],
  })
    .then(res => {
      const { error } = res.deleteCoachingSession;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Session successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete session');
    });
}

export function createNewCoach(userUuid: string, subjects: string[], hourlyRateInCents: number) {
  const errorText = `CoachingActions - could not create new coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<CreateCoach, CreateCoachVariables>({
    mutation: CREATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      hourlyRateInCents,
    },
    refetchQueries: [
      {
        query: GET_COACH_LIST_QUERY,
      },
    ],
  })
    .then(res => {
      const { error } = res.createCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function getNameForUserUuid(userUuid: string) {
  return RemindAPI.execute<GetNameForUserUuid, GetNameForUserUuidVariables>({
    query: GET_NAME_FOR_USER_UUID_QUERY,
    variables: {
      userUuid,
    },
  })
    .then(res => res.getNameForUserUuid)
    .catch(() => {
      ToastActions.showErrorToast('Could not find user’s name');
      return undefined;
    });
}

export function purchaseCoaching(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  tokenizedCard: string,
  purchaserUuid: string,
  timezone: string,
  subjects: string[],
  student: Partial<Student> & { name: string },
  studentGradeLevel: string
): Promise<{ participantUuid: string | null }> {
  const errorText = (err: any): string =>
    `CoachingActions - could not purchase coaching purchaserUuid=${purchaserUuid} studentUuid=${student.uuid} studentName=${student.name} coachUuid=${coachUuid} startDateTime=${startDateTime} error=${err}`;
  const sharedVars = {
    startDateTime,
    endDateTime,
    coachUuid,
    tokenizedCard,
    purchaserUuid,
    timezone,
    subjects,
    studentGradeLevel,
  };
  const promise = student.uuid
    ? RemindAPI.execute<
        PurchaseCoachingProductWithConnectedStudent,
        PurchaseCoachingProductWithConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentUuid: student.uuid },
      }).then(res => ({
        participantUuid: res.purchaseCoachingSessionWithConnectedStudent.participantUuid,
      }))
    : RemindAPI.execute<
        PurchaseCoachingProductWithNoConnectedStudent,
        PurchaseCoachingProductWithNoConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentName: student.name },
      }).then(res => ({
        participantUuid: res.purchaseCoachingSessionWithNoConnectedStudent.participantUuid,
      }));

  return promise.catch(err => {
    console.warn(err);
    console.error(errorText(err));
    return { participantUuid: null };
  });
}

export function patchParticipant(uuid: string, participant: CoachingParticipantInput) {
  const errorText = `CoachingActions - could not create modify participant uuid=${uuid}`;
  return RemindAPI.execute<PatchParticipant, PatchParticipantVariables>({
    mutation: PATCH_PARTICIPANT_MUTATION,
    variables: {
      uuid,
      participant,
    },
  })
    .then(res => {
      const { error } = res.patchParticipant;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateParticipant(uuid, participant);
      ToastActions.showSuccessToast('Participant successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify participant');
      return { success: false };
    });
}

export function patchCoach(uuid: string, coach: CoachingCoachInput) {
  const errorText = `CoachingActions - could not create modify coach uuid=${uuid}`;
  return RemindAPI.execute<PatchCoach, PatchCoachVariables>({
    mutation: PATCH_COACH_MUTATION,
    variables: {
      uuid,
      coach,
    },
  })
    .then(res => {
      const { error } = res.patchCoach;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateCoach(uuid, coach);
      ToastActions.showSuccessToast('Coach successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify coach');
      return { success: false };
    });
}

export function findOrCreateStreamForStudentUuid(studentUuid: string) {
  const errorText = `CoachingActions - could not create create coaching stream for studentUuid=${studentUuid}`;
  return RemindAPI.execute<FindOrCreateCoachingStream, FindOrCreateCoachingStreamVariables>({
    mutation: FIND_OR_CREATE_COACHING_STREAM_MUTATION,
    variables: {
      userUuid: studentUuid,
    },
  })
    .then(res => {
      const { error } = res.findOrCreateCoachingStream;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      ToastActions.showSuccessToast('Coaching chat successfully created');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not create coaching chat');
      return { success: false };
    });
}

export function addStudentByDevice(
  deviceAddress: string,
  participantUUID: string,
  userEmail?: string
) {
  const errorText = `CoachingActions - could not add student with device=${deviceAddress} for participant=${participantUUID}`;
  return RemindAPI.execute<AddStudentToCoachingByDevice, AddStudentToCoachingByDeviceVariables>({
    mutation: ADD_STUDENT_MUTATION,
    variables: {
      deviceAddress,
      participantUUID,
    },
  })
    .then(res => {
      const { error } = res.addStudentToCoachingByDevice;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      ToastActions.showSuccessToast(
        `We’ve sent an invitation to ${deviceAddress}${userEmail &&
          ` and a confirmation to ${userEmail}`}`
      );
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(
        'We were unable to add your student’s account at this time. Please email us at coaching@remind101.com for further assistance.'
      );
      return { success: false };
    });
}

function updateParticipant(uuid: string, participantInput: CoachingParticipantInput) {
  // updates a specific participant in the Apollo cache
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'CoachingParticipant', uuid }),
    fragment: gql`
      fragment participant on CoachingParticipant {
        studentName
        studentUuid
        whiteboardUrl
      }
    `,
    variables: { uuid },
    data: {
      studentUuid: participantInput.studentUuid,
      studentName: participantInput.studentName,
      whiteboardUrl: participantInput.whiteboardUrl,
      __typename: 'CoachingParticipant',
    },
  });
}

function updateCoach(uuid: string, coachInput: CoachingCoachInput) {
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'Coach', uuid }),
    fragment: gql`
      fragment coach on Coach {
        status
      }
    `,
    variables: { uuid },
    data: {
      status: coachInput.status,
      __typename: 'Coach',
    },
  });
}
